import { ChargeConstant } from "presentation/constant/ChargeHeader/ChargeConstant";
import { ManualChargeConstant } from "presentation/constant/ManualCharge/ManualChargeConstant";
import { useManualChargeDetailCntrVM } from "presentation/hook/ManualCharge/useManualChargeDetailCntrVM";
import { useManualChargeDetailTracked } from "presentation/store/ManualCharge/ManualChargeDetailProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useMemo } from "react";
import { FieldType, HPHInputMask, HPHInputTextarea, IFieldValue, InputField } from "veronica-ui-component/dist/component/core";

const ManualChargeCntrSearchForm = () => {
    const [manualChargeDetailState] = useManualChargeDetailTracked();
    const manualChargeCntrVM = useManualChargeDetailCntrVM();
    const cntrSearchCriteria = manualChargeDetailState.manualChargeDetailCntrState.searchCriteria;

    const memoCoVslVoy = useMemo(() =>
        <div className='im-flex-row-item'>
            <HPHInputMask
                label={ManualChargeConstant.Header.CO_VSL_VOY}
                width={'240px'}
                value={{
                    co: cntrSearchCriteria.co ?? '',
                    vsl: cntrSearchCriteria.vsl ?? '',
                    voy: cntrSearchCriteria.voy ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => manualChargeCntrVM.onCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy'
                })} />
        </div>
        , [cntrSearchCriteria.co, cntrSearchCriteria.vsl, cntrSearchCriteria.voy, manualChargeCntrVM])

    const memoVslName = useMemo(() =>
        <div className='im-flex-row-item'>
            <InputField
                width='240px'
                maxLength={30}
                label={ChargeConstant.ChargeHeader.VESSEL_NAME}
                type="text"
                value={cntrSearchCriteria.vesselName || ''}
                onChange={(e) => manualChargeCntrVM.onInputTextChange(e, 'vesselName')}
                allowedSpecialChars={'all'} />
        </div>
        , [cntrSearchCriteria.vesselName, manualChargeCntrVM])

    const memoEtdFromTo = useMemo(() =>
        <div className='im-flex-row-item'>
            <DateRangePickerComponent
                label={ChargeConstant.Charge.ETD_RANGE}
                width='240px'
                fieldNames={{ startField: "etdDateFrom", endField: "etdDateTo" }}
                dateRange={{ startDate: cntrSearchCriteria.etdDateFrom, endDate: cntrSearchCriteria.etdDateTo }}
                onDatesChange={manualChargeCntrVM.onDateRangeChange}
            />
        </div>
        , [cntrSearchCriteria.etdDateFrom, cntrSearchCriteria.etdDateTo, manualChargeCntrVM.onDateRangeChange])

    const memoEtaFromTo = useMemo(() =>
        <div className='im-flex-row-item'>
            <DateRangePickerComponent
                label={ChargeConstant.ChargeHeader.ETA_RANGE}
                width='240px'
                fieldNames={{ startField: "etaDateFrom", endField: "etaDateTo" }}
                dateRange={{ startDate: cntrSearchCriteria.etaDateFrom, endDate: cntrSearchCriteria.etaDateTo }}
                onDatesChange={manualChargeCntrVM.onDateRangeChange}
            />
        </div>
        , [cntrSearchCriteria.etaDateFrom, cntrSearchCriteria.etaDateTo, manualChargeCntrVM.onDateRangeChange])

    const memoCntrNo = useMemo(() =>
        <div className='im-flex-row-item'>
            <HPHInputTextarea
                label={ChargeConstant.Charge.CNTR_NO}
                width="240px"
                rows={3}
                resizable={false}
                helpIcon={true}
                toolTipText={"Use comma ( , ) as delimiter"}
                value={cntrSearchCriteria.cntrList?.join(",")}
                onChange={(e) => { manualChargeCntrVM.onTextAreaChange(e, "cntrList", true) }} />
        </div>
        , [cntrSearchCriteria.cntrList, manualChargeCntrVM])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "330px" }}>

            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={cntrSearchCriteria?.billToCompList?.join(',')}
                fieldValue={cntrSearchCriteria?.billToCompList}
                fieldLabel={ManualChargeConstant.Header.BILL_TO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={manualChargeDetailState.dynamicOptions.billToCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    manualChargeCntrVM.onCntrSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [cntrSearchCriteria.billToCompList, manualChargeCntrVM, manualChargeDetailState.dynamicOptions.billToCompDropdownOptions])

    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "330px" }}>

            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={cntrSearchCriteria?.chargeOnCompList?.join(',')}
                fieldValue={cntrSearchCriteria?.chargeOnCompList}
                fieldLabel={ChargeConstant.Charge.CHARGE_ON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeOnCompList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={manualChargeDetailState.dynamicOptions.chargeOnCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    manualChargeCntrVM.onCntrSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [cntrSearchCriteria.chargeOnCompList, manualChargeCntrVM, manualChargeDetailState.dynamicOptions.chargeOnCompDropdownOptions])

    return <>

        <CriteriaItemContainer>
            {memoCoVslVoy}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoEtdFromTo}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoEtaFromTo}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoBillToCompany}
        </CriteriaItemContainer>


        <CriteriaItemContainer>
            {memoChargeOnCompany}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoCntrNo}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoVslName}
        </CriteriaItemContainer>
    </>;
}
export default memo(ManualChargeCntrSearchForm);